import { useContext, useEffect, useRef, useState } from "react";
import { Transition } from "react-transition-group";
import PropTypes from "prop-types";
import { debounce } from "lodash";

import { PanelContext } from "./PanelContext";

const PANEL_ANIMATION_DURATION_MS = 300;

const containerAnimationStyles = (duration) => ({
  transition: `max-height ${duration}ms ease-in-out`,
});

const containerTransitionStyles = (nodeHeight, allowCollapse) =>
  allowCollapse
    ? {
        entering: { maxHeight: `${nodeHeight}px`, overflow: "hidden" },
        entered: { maxHeight: `${nodeHeight}px` },
        exiting: { maxHeight: "0", overflow: "hidden" },
        exited: { maxHeight: "0" },
      }
    : {};

const PanelCollapse = ({
  allowCollapse,
  className,
  collapsed: collapsedFromProps,
  children,
  transitionDuration,
  ...props
}) => {
  const nodeRef = useRef(null);
  const [nodeHeight, setNodeHeight] = useState(0);
  const { collapsed: collapsedFromContext } = useContext(PanelContext);
  const collapsed = typeof collapsedFromProps === "boolean" ? collapsedFromProps : collapsedFromContext;

  useEffect(() => {
    const onResize = debounce(() => {
      if (nodeRef.current) setNodeHeight(nodeRef.current.scrollHeight);
    }, 100);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return (
    <Transition
      nodeRef={nodeRef}
      in={!collapsed}
      appear
      timeout={transitionDuration}
      unmountOnExit
      mountOnEnter
    >
      {(panelContainerState) => (
        <div
          {...props}
          ref={(node) => {
            nodeRef.current = node;
            setNodeHeight(node?.scrollHeight || 0);
          }}
          className={className}
          data-js-collapsed={collapsed}
          style={{
            ...containerAnimationStyles(transitionDuration),
            ...containerTransitionStyles(nodeHeight, allowCollapse)[panelContainerState],
          }}
        >
          {children}
        </div>
      )}
    </Transition>
  );
};

PanelCollapse.propTypes = {
  allowCollapse: PropTypes.bool,
  className: PropTypes.string,
  collapsed: PropTypes.bool,
  children: PropTypes.node,
  transitionDuration: PropTypes.number,
};

PanelCollapse.defaultProps = {
  allowCollapse: true,
  className: "",
  collapsed: null,
  children: null,
  transitionDuration: PANEL_ANIMATION_DURATION_MS,
};

export default PanelCollapse;
